// Leitura de código de barras pela CÂMERA — a parte pura e testável.
//
// POR QUE BarcodeDetector NATIVO E NÃO UMA BIBLIOTECA
// A CSP do sistema é `script-src 'self'` sem `unsafe-eval` e SEM
// `wasm-unsafe-eval`. Os decodificadores populares (ZXing, quagga) carregam
// WebAssembly ou avaliam código — os dois esbarrariam nela, e afrouxar a CSP
// para ganhar um scanner seria trocar segurança real por conveniência. O
// BarcodeDetector é do próprio navegador: nada baixa, nada avalia, e a decisão
// de CSP fica intacta.
//
// O PREÇO É HONESTO: no iPhone/Safari a API não existe. Lá o botão de câmera
// simplesmente não aparece — o leitor USB e a digitação continuam funcionando,
// que é como o balcão trabalha hoje. Melhor um botão ausente do que um botão
// que abre a câmera e nunca lê nada.

/**
 * O que aceitamos ler. Os três primeiros cobrem quase tudo no balcão: o nosso
 * Code 128 (etiqueta da casa) e o EAN das peças que vêm com código de fábrica.
 */
export const FORMATOS_ACEITOS = [
  'code_128', 'ean_13', 'ean_8', 'code_39', 'upc_a', 'upc_e',
] as const;

export interface DeteccaoBarcode {
  rawValue: string;
  format: string;
}

/** O construtor que o navegador expõe — não existe no lib.dom do TypeScript. */
interface ConstrutorBarcodeDetector {
  new (opcoes?: { formats?: string[] }): {
    detect(fonte: CanvasImageSource): Promise<DeteccaoBarcode[]>;
  };
  getSupportedFormats(): Promise<string[]>;
}

declare global {
  // eslint-disable-next-line no-var, vars-on-top
  var BarcodeDetector: ConstrutorBarcodeDetector | undefined;
}

export type SuporteScanner = 'ok' | 'sem-api' | 'sem-camera';

/**
 * Se dá para oferecer o botão de câmera nesta máquina.
 *
 * NÃO basta checar `'BarcodeDetector' in window`: há Chrome (notadamente no
 * Windows) em que a classe existe e `getSupportedFormats()` volta VAZIA — o
 * botão apareceria e a leitura nunca aconteceria. Por isso a checagem é pela
 * lista de formatos, e exigindo `code_128`, que é o da nossa etiqueta.
 */
export async function suporteScanner(): Promise<SuporteScanner> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'sem-camera';
  }
  const Detector = globalThis.BarcodeDetector;
  if (!Detector) return 'sem-api';
  try {
    const formatos = await Detector.getSupportedFormats();
    return formatos.includes('code_128') ? 'ok' : 'sem-api';
  } catch {
    return 'sem-api';
  }
}

/** Um caractere que pode aparecer num código impresso (ASCII visível). */
function imprimivel(ch: string): boolean {
  const c = ch.charCodeAt(0);
  return c >= 32 && c !== 127;
}

/**
 * Limpa a leitura crua. `null` significa "ignore este quadro".
 *
 * Caracteres de controle saem porque o leitor às vezes devolve o terminador
 * grudado no código (CR, LF, NUL). A filtragem é por código, e não por regex
 * de faixa: regex com caractere de controle literal é o tipo de coisa que um
 * editor "corrige" sem avisar, e aí ela para de filtrar em silêncio.
 *
 * O piso de 3 caracteres úteis é o mesmo de `codigoBateExato` (bip.ts): abaixo
 * disso é ruído de detecção, não código.
 */
export function leituraValida(bruta: string): string | null {
  const limpa = Array.from(bruta ?? '').filter(imprimivel).join('').trim();
  return limpa.replace(/[^A-Za-z0-9]/g, '').length >= 3 ? limpa : null;
}

/**
 * Só age quando o MESMO código aparece em dois quadros seguidos.
 *
 * Sem isso, um quadro tremido produz uma leitura parcial que vira busca errada
 * — e na tela de Venda a peça errada entra sozinha no carrinho. Exigir a
 * repetição custa ~150 ms e elimina a classe inteira desse erro.
 */
export function confirmarLeitura(anterior: string | null, atual: string): boolean {
  return anterior !== null && anterior === atual;
}
